import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigationType, useOutlet } from 'react-router-dom';

/* eslint-disable react-hooks/refs -- 缓存出口需在渲染期读取/更新上一路由的 pathname 与 outlet 快照（与渲染期维护缓存 Map 同一模式） */

export const KEEP_ALIVE_REFRESH_STATE_KEY = 'refreshKeepAlive';
/** 跳转 state 携带该字段时，主动缓存被离开的页面（优先于自动策略） */
export const KEEP_ALIVE_CACHE_STATE_KEY = 'keepAliveCache';

interface CachedOutlet {
  outlet: ReactNode;
  version: number;
}

const getStateFlag = (state: unknown, key: string) => {
  if (typeof state !== 'object' || state === null) return false;
  return (state as Record<string, unknown>)[key] === true;
};

const shouldRefreshKeepAlive = (state: unknown) =>
  getStateFlag(state, KEEP_ALIVE_REFRESH_STATE_KEY);

const shouldCacheKeepAlive = (state: unknown) => getStateFlag(state, KEEP_ALIVE_CACHE_STATE_KEY);

const containsNavigate = (node: ReactNode): boolean => {
  if (node === null || typeof node !== 'object') return false;
  const element = node as ReactElement;
  if (element.type === Navigate) return true;
  const { children } = (element.props as { children?: ReactNode }) ?? {};
  if (Array.isArray(children)) return children.some((child) => containsNavigate(child));
  return containsNavigate(children);
};

/**
 * 路由级组件缓存出口（支持两种机制）：
 * 1. 自动：
 * - 区域首页进入时自动缓存，子路由跳转不淘汰，离开该区域才替换；
 * - 注意：子路由跳转如果携带query参数会导致 pathname 不同从而无法命中缓存。
 * 2. 显式：
 * - PUSH/REPLACE 跳转 state 携带 KEEP_ALIVE_CACHE_STATE_KEY 时，缓存被离开的页面。
 * - 可以使用useKeepAliveNavigate hooks语法糖，详见 useKeepAliveNavigate hook。
 * - 缓存键仅按 pathname，不带 query；带 query 的子路径请用显式声明。
 */
export function KeepAliveOutlet() {
  const outlet = useOutlet();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [cachedOutletMap] = useState(() => new Map<string, CachedOutlet>());
  const [refreshedLocationKeys] = useState(() => new Set<string>());
  // 记录上一路由的 pathname + outlet（初始为 null，首帧视为一次导航）
  const prevRef = useRef<{ pathname: string; outlet: ReactNode } | null>(null);

  const isNewNavigation = prevRef.current?.pathname !== location.pathname;

  let cachedCacheKey = cachedOutletMap.keys().next().value as string | undefined;

  if (isNewNavigation) {
    const prev = prevRef.current;
    const isInsideCachedRoute =
      !!cachedCacheKey &&
      (location.pathname === cachedCacheKey || location.pathname.startsWith(`${cachedCacheKey}/`));
    const returningToCachedPage = cachedCacheKey === location.pathname;

    if (
      prev?.outlet &&
      !containsNavigate(prev.outlet) &&
      !returningToCachedPage &&
      navigationType !== 'POP' &&
      shouldCacheKeepAlive(location.state)
    ) {
      // 显式优先：缓存被离开的页面，替换自动缓存
      const prevVersion = cachedOutletMap.get(prev.pathname)?.version ?? 0;
      cachedOutletMap.clear();
      cachedOutletMap.set(prev.pathname, { outlet: prev.outlet, version: prevVersion });
      cachedCacheKey = prev.pathname;
    } else if (cachedCacheKey && !isInsideCachedRoute) {
      // 自动淘汰：离开当前缓存区域，清除旧缓存
      cachedOutletMap.clear();
      cachedCacheKey = undefined;
    }

    if (!cachedCacheKey && outlet && !containsNavigate(outlet)) {
      // 自动缓存：区域首屏成为缓存页
      if (shouldRefreshKeepAlive(location.state)) {
        refreshedLocationKeys.add(location.key);
      }
      cachedOutletMap.set(location.pathname, { outlet, version: 0 });
      cachedCacheKey = location.pathname;
    }
  }

  prevRef.current = { pathname: location.pathname, outlet };

  const activeCacheKey = cachedCacheKey === location.pathname ? cachedCacheKey : null;
  const activeCachedOutlet = activeCacheKey ? cachedOutletMap.get(activeCacheKey) : null;
  const shouldRefresh =
    activeCacheKey &&
    outlet &&
    activeCachedOutlet &&
    navigationType !== 'POP' &&
    shouldRefreshKeepAlive(location.state) &&
    !refreshedLocationKeys.has(location.key);

  if (shouldRefresh && activeCacheKey && activeCachedOutlet) {
    refreshedLocationKeys.add(location.key);
    cachedOutletMap.clear();
    cachedOutletMap.set(activeCacheKey, {
      outlet,
      version: activeCachedOutlet.version + 1,
    });
  }

  const cachedEntry =
    activeCachedOutlet ?? (cachedCacheKey ? cachedOutletMap.get(cachedCacheKey) : null);

  return (
    <>
      {cachedEntry && !activeCacheKey && (
        <div key={`keep:${cachedCacheKey}:${cachedEntry.version}`} className="hidden">
          {cachedEntry.outlet}
        </div>
      )}
      <div
        key={`keep:${location.pathname}:${activeCachedOutlet ? activeCachedOutlet.version : 0}`}
        className="contents"
      >
        {activeCachedOutlet ? activeCachedOutlet.outlet : outlet}
      </div>
    </>
  );
}
